import {Injectable} from "@angular/core";
import {Local} from "../config-support/config";
import {LocalConfigurationService} from "@fedelta-pos/data-client";
import {ResponsiveService} from "./responsive.service";
import {SessionModel} from "./session.model";
import {ParamConfig, ParamConfigService} from "../config-support/param-config.service";
import {AppModuleConstants} from "../app.module.constants";
import {OrderModel} from "../order/order.model";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {PaymentType, WalletProvider} from "../payment/payment";
import {DisplayableOrderItem, OrderItem, OrderType} from "../order/order";
import {MenuItemData} from "../menu/menu-entity.model";
import {PaymentModel} from "../payment/payment.model";

declare let gtag: any;

export enum LandingPage {
    ORDERING = <any>"ordering",
    LOGIN = <any>"login",
    STORE_SELECTION = <any>"store selection"
}

// noinspection JSUnusedGlobalSymbols
export class PromoShown {
    constructor(public promoName: string, public promoType: string, public itemName: string, public itemPrice: number, public itemPLU: string) {
    }
}

export class OrderPlacedEvent {
    public orderType: "pickup" | "table" | "delivery";
    public login: "guest" | "member";
    public amount: number;
    public transferredAmount: number;
    public walletPayment: boolean;

    // noinspection JSUnusedGlobalSymbols
    constructor(orderType: OrderType,
                guest: boolean,
                paymentAmount: number,
                public paymentType: PaymentType,
                public itemsOrdered: number,
                public walletPaymentAvailable: boolean,
                public quickPay: boolean,
                public tableNumber: string) {
        this.orderType = orderType === OrderType.ORDER_AHEAD ? "pickup" : orderType === OrderType.ORDER_AT_TABLE ? "table" : "delivery";
        this.login = guest ? "guest" : "member";
        this.amount = paymentType === PaymentType.GROUP_PAY ? 0 : paymentAmount;
        this.transferredAmount = paymentType === PaymentType.GROUP_PAY ? paymentAmount : 0;
        this.walletPayment = paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY;
    }
}

@Injectable()
export class AnalyticsService {
    // noinspection SpellCheckingInspection
    private static readonly GOOGLE_NON_PROD_KEY: string = "G-LJLEVLQ95C";
    // noinspection SpellCheckingInspection
    private static readonly GOOGLE_PROD_KEY: string = "G-GPGKRGYCRG";

    private static readonly ANALYTICS_VERSION: number = 3;

    private appName: string;

    private clientId: string;

    /**
     * App start should only be tracked once
     */
    private appStartTracked: boolean;

    private scanType: string;

    private initialised: boolean;

    /**
     * Indicates if analytics is being captured for this session
     */
    private analyticsEnabled: boolean;

    private walletPaymentTracked: boolean;

    private _sessionModel: SessionModel;
    private _orderModel: OrderModel;
    private _paymentModel: PaymentModel;
    private _orderingContextModel: OrderingContextModel;

    private promosShown: PromoShown[] = [];

    private metaPixelLoaded: boolean;
    private googleTagLoaded: boolean;

    private googleKey: string;

    constructor(public localConfigurationService: LocalConfigurationService,
                private responsiveService: ResponsiveService,
                private paramConfigService: ParamConfigService) {
        console.log("--- AnalyticsService");
    }

    public set sessionModel(sessionModel: SessionModel) {
        this._sessionModel = sessionModel;
    }

    public set paymentModel(paymentModel: PaymentModel) {
        this._paymentModel = paymentModel;
    }

    public set orderModel(orderModel: OrderModel) {
        this._orderModel = orderModel;
    }

    public set orderingContextModel(orderingContextModel: OrderingContextModel) {
        this._orderingContextModel = orderingContextModel;
    }

    private async checkInitialised(): Promise<void> {
        if (!this.initialised) {
            await this.init();
            this.initialised = true;
        }
    }

    private async init(): Promise<void> {
        this.analyticsEnabled = !this._sessionModel.previewToken;
        if (!this.analyticsEnabled) {
            return;
        }

        this.appName = await this.localConfigurationService.getString(Local.APP_NAME);
        const dataServerUrl: string = await this.localConfigurationService.getString(Local.DATASERVER_URL);

        // Initialise Google Analytics
        if (!dataServerUrl || dataServerUrl.indexOf("localhost") > 0 || dataServerUrl.indexOf(".dev.") > 0) {
            this.googleKey = AnalyticsService.GOOGLE_NON_PROD_KEY;
            this.clientId = "dev";
        } else if (dataServerUrl.startsWith("https://") && dataServerUrl.indexOf("chewzie.net") > 0) {
            let clientId = dataServerUrl.substring("https://".length);
            const split: string[] = clientId.split(".");
            if (split.length > 0) {
                clientId = split[0];
            }

            if (clientId.endsWith("-who")) {
                clientId = clientId.substring(0, clientId.length - "-who".length);
                this.appName = clientId;
            }

            if (clientId.indexOf("-") > 0 || dataServerUrl.indexOf(".stage.") > 0) {
                this.googleKey = AnalyticsService.GOOGLE_NON_PROD_KEY;
            } else {
                if (this._sessionModel.currentApp && this._sessionModel.currentApp.appLiveMode) {
                    this.googleKey = AnalyticsService.GOOGLE_PROD_KEY;
                } else {
                    this.googleKey = AnalyticsService.GOOGLE_NON_PROD_KEY;
                }
            }
            this.clientId = clientId;
        }

        gtag("config", this.googleKey);
    }

    initMetaPixel(pixelId: string): void {
        if (!this.metaPixelLoaded) {
            window["fbq"]("init", pixelId);
            window["fbq"]("track", "PageView");
            this.metaPixelLoaded = true;
        }
    }

    initGoogleTag(tagId: string): void {
        if (!this.googleTagLoaded) {
            window["dataLayer"] = window["dataLayer"] || [];
            const gtag = function (type, value) {
                window["dataLayer"].push(arguments);
            };
            gtag("config", tagId);
            this.googleTagLoaded = true;
        }
    }

    async trackLandingPage(landingPage: LandingPage): Promise<void> {
        await this.checkInitialised();
        if (!this.appStartTracked) {
            this.appStartTracked = true;

            const browserRefresh: boolean = (window.performance && window.performance.getEntriesByType("navigation").map((nav) => nav.type).includes("reload"));

            const paramConfig: ParamConfig = await this.paramConfigService.getConfig();

            let scanType: string;
            if (this.responsiveService.isApp || paramConfig.nativeMode) {
                scanType = "n/a";
            } else {
                scanType = paramConfig.scanType;
            }

            if (!scanType) {
                scanType = "none";
            }
            this.scanType = scanType;

            const props: any = this.commonProps(paramConfig);
            props.cz_table_number = paramConfig.tableNumber;
            props.cz_table_only_mode = paramConfig.tableOnlyMode;
            props.cz_pickup_only_mode = paramConfig.pickupOnlyMode;
            props.cz_entry_scan_type = this.scanType;
            props.cz_browser_refresh = browserRefresh;
            props.cz_landing_page = landingPage;
            props.cz_integrated_loyalty = this._sessionModel.currentApp.integratedLoyalty;
            this.sendEvent("cz_enter_app", props);
        }
    }

    async trackViewItem(menuItem: MenuItemData): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_price = menuItem.sellable.defaultPrice;
        props.cz_item_name = menuItem.sellable.name;
        props.cz_item_plu = menuItem.sellable.plu;
        props.cz_item_sellable_id = menuItem.sellable.sellableId;
        props.cz_promoted_feature_tag = menuItem.promotedFeatureTag;
        this.sendEvent("cz_item_viewed", props);
    }

    async trackItemAddedToOrder(orderItem: OrderItem, promotedFeatureTag?: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_from_deal = orderItem.deal;
        props.cz_item_price = orderItem.price;
        props.cz_item_total = orderItem.localTotal;
        props.cz_item_quantity = orderItem.quantity;
        props.cz_item_name = orderItem.name;
        props.cz_item_plu = orderItem.sellable.plu;
        if (orderItem.sellable) {
            props.cz_item_sellable_id = orderItem.sellable.sellableId;
        }
        props.cz_promoted_feature_tag = promotedFeatureTag;
        this.sendEvent("cz_item_ordered", props);
    }

    async trackEditItem(orderItem: DisplayableOrderItem): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_name = orderItem.name;
        if (orderItem instanceof OrderItem) {
            props.cz_item_price = orderItem.price;
            props.cz_item_plu = orderItem.sellable.plu;
            props.cz_item_sellable_id = orderItem.sellable.sellableId;
            props.cz_item_total = orderItem.localTotal;
            props.cz_item_quantity = orderItem.quantity;
        }
        this.sendEvent("cz_item_edit", props);
    }

    async trackItemEdited(orderItem: DisplayableOrderItem): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_name = orderItem.name;
        if (orderItem instanceof OrderItem) {
            props.cz_item_price = orderItem.price;
            props.cz_item_plu = orderItem.sellable.plu;
            props.cz_item_sellable_id = orderItem.sellable.sellableId;
            props.cz_item_total = orderItem.localTotal;
            props.cz_item_quantity = orderItem.quantity;
        }
        this.sendEvent("cz_item_edited", props);
    }

    async trackItemQtyChanged(orderItem: DisplayableOrderItem): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_name = orderItem.name;
        if (orderItem instanceof OrderItem) {
            props.cz_item_plu = orderItem.sellable.plu;
            props.cz_item_sellable_id = orderItem.sellable.sellableId;
            props.cz_item_quantity = orderItem.quantity;
        }
        this.sendEvent("cz_item_qty_changed", props);
    }

    async trackItemRemoved(orderItem: DisplayableOrderItem): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_name = orderItem.name;
        if (orderItem instanceof OrderItem) {
            props.cz_item_price = orderItem.price;
            props.cz_item_plu = orderItem.sellable.plu;
            props.cz_item_sellable_id = orderItem.sellable.sellableId;
            props.cz_item_total = orderItem.localTotal;
            props.cz_item_quantity = orderItem.quantity;
        }
        this.sendEvent("cz_item_removed", props);
    }

    async track3DSStart(amount: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_amount = amount;
        this.sendEvent("cz_3ds_check_start", props);
    }

    async track3DSFailedOnClient(amount: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_amount = amount;
        props.cz_failure_location = "client";
        this.sendEvent("cz_3ds_check_failed", props);
    }

    async track3DSFailedOnServer(amount: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_amount = amount;
        props.cz_failure_location = "server";
        this.sendEvent("cz_3ds_check_failed", props);
    }

    async track3DSSucceeded(amount: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_amount = amount;
        this.sendEvent("cz_3ds_check_complete", props);
    }

    async trackNotReadyToPlaceOrder(): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        if (this._orderModel.orderErrorStates.emptyOrderAttempted) {
            props.cz_reject_order_reason = "emptyOrderAttempted";
        } else if (this._orderModel.orderErrorStates.orderWithInvalidTimeAttempted) {
            props.cz_reject_order_reason = this._orderModel.initiatedOrder.unavailabilityReason === "offline" ? "storeOffline"
                : this._orderingContextModel.orderType === OrderType.ORDER_AT_TABLE ? "storeUnavailableForTableOrders"
                    : !this._orderingContextModel.canShowAsapSelectionTime && !this._orderingContextModel.expectedCompletion ? "noTimeSelected"
                        : "invalidTime";
        } else if (this._orderModel.orderErrorStates.minMaxOrderAmountAttempted) {
            props.cz_reject_order_reason = "minMaxOrderAmountAttempted";
        } else {
            props.cz_reject_order_reason = "unknown";
        }

        this.addStoreStatusProp(props);
        this.sendEvent("cz_order_not_ready", props);
    }

    async trackDelay(action: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_action = action;
        this.sendEvent("cz_unexpected_delay", props);
    }

    async trackAnomaly(description: string, error?: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_description = description;
        props.cz_error = error;
        this.sendEvent("cz_unexpected_anomaly", props);
    }

    async trackWalletPaymentRequestStarted(walletProvider: WalletProvider): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = walletProvider;
        this.sendEvent("cz_wallet_payment_started", props);
    }

    async trackWalletPaymentRequestSucceeded(walletProvider: WalletProvider): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = walletProvider;
        this.sendEvent("cz_wallet_payment_succeeded", props);
    }

    async trackWalletPaymentRequestCancelled(walletProvider: WalletProvider): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = walletProvider;
        this.sendEvent("cz_wallet_payment_cancelled", props);
    }

    async trackPlaceOrderStarted(event: OrderPlacedEvent): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = event.paymentType;
        props.cz_wallet_wayment = event.walletPayment;
        props.cz_wallet_payment_available = event.walletPaymentAvailable;
        props.cz_quick_pay = event.quickPay;
        props.paymentType = event.paymentType;
        props.cz_login = event.login;
        props.cz_items_ordered = event.itemsOrdered;
        props.cz_order_amount = event.amount;
        props.cz_entry_scan_type = this.scanType;
        props.cz_order_type = event.orderType;
        props.cz_table_number = event.tableNumber;
        props.cz_table_only_mode = paramConfig.tableOnlyMode;
        props.cz_pickup_only_mode = paramConfig.pickupOnlyMode;
        this.sendEvent("cz_place_order_start", props);
    }

    async trackPlaceOrderAbandoned(stage: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_abandoned_stage = stage;
        this.sendEvent("cz_place_order_abandoned", props);
    }

    async trackPlaceOrderFailed(reason: string, errorCode?: string, gatewayCode?: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_order_failed_reason = reason;
        props.cz_order_failed_error_code = errorCode;
        props.cz_order_failed_gateway_code = gatewayCode;
        this.sendEvent("cz_place_order_failed", props);
    }

    async trackOrderPlaced(event: OrderPlacedEvent): Promise<void> {
        await this.checkInitialised();
        // Revenue
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_payment_type = event.paymentType;
        props.cz_wallet_payment = event.walletPayment;
        props.cz_wallet_payment_available = event.walletPaymentAvailable;
        props.cz_quick_pay = event.quickPay;
        props.cz_payment_type = event.paymentType;
        props.cz_login = event.login;
        props.cz_items_ordered = event.itemsOrdered;
        props.cz_order_amount = event.amount;
        props.cz_entry_scan_type = this.scanType;
        props.cz_order_type = event.orderType;
        props.cz_table_number = event.tableNumber;
        props.cz_table_only_mode = paramConfig.tableOnlyMode;
        props.cz_pickup_only_mode = paramConfig.pickupOnlyMode;
        this.sendEvent("cz_place_order_complete", props);
    }

    /**
     * Track if Apple/Google Pay is available for a user
     */
    public async trackWalletPaymentStatus(walletPaymentAvailable: boolean): Promise<void> {
        await this.checkInitialised();
        if (this.initialised && !this.walletPaymentTracked) {
            this.walletPaymentTracked = true;
            const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
            const props: any = this.commonProps(paramConfig);
            if (walletPaymentAvailable) {
                this.sendEvent("cz_wallet_payment_available", props);
            } else {
                this.sendEvent("cz_wallet_payment_unavailable", props);
            }
        }
    }

    async trackSetStore(): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        this.addStoreStatusProp(props);
        this.sendEvent("cz_set_store", props);
    }

    async trackReorderRoundShown(quickPayAvailable: boolean, itemTotal: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_quick_pay_available = quickPayAvailable;
        props.cz_item_total = itemTotal;
        this.sendEvent("cz_reorder_round_shown", props);
    }

    async trackReorderRoundAdded(itemTotal: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_total = itemTotal;
        this.sendEvent("cz_reorder_round_added", props);
    }

    async trackReorderRoundPaid(itemTotal: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_total = itemTotal;
        this.sendEvent("cz_reorder_round_paid", props);
    }

    async trackReorderRoundCancelled(itemTotal: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_item_total = itemTotal;
        this.sendEvent("cz_reorder_round_cancelled", props);
    }

    async trackPromoShown(promoName: string, promoType: string, itemName: string, itemPrice: number, itemPLU: string): Promise<void> {
        await this.checkInitialised();

        // Only track card promos once per session (or once per session per promoted product)
        if (promoType === "card") {
            if (itemName) {
                if (this.promosShown.find((shown: PromoShown) => shown.promoName === promoName && shown.promoType === promoType && shown.itemName === itemName && shown.itemPLU === itemPLU)) {
                    return;
                }
            } else {
                if (this.promosShown.find((shown: PromoShown) => shown.promoName === promoName && shown.promoType === promoType)) {
                    return;
                }
            }
        }
        this.promosShown.push(new PromoShown(promoName, promoType, itemName, itemPrice, itemPLU));

        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_name = promoName;
        props.cz_promo_type = promoType;
        props.cz_item_name = itemName;
        props.cz_item_price = itemPrice;
        props.cz_item_plu = itemPLU;
        this.sendEvent("cz_promo_shown", props);
    }

    async trackPromoItemsShown(promoName: string, promoType: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_name = promoName;
        props.cz_promo_type = promoType;
        this.sendEvent("cz_promo_items_shown", props);
    }

    async trackPromoItemOrdered(promoName: string, promoType: string, itemName: string, itemPrice: number, itemPLU: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_name = promoName;
        props.cz_promo_type = promoType;
        props.cz_item_name = itemName;
        props.cz_item_price = itemPrice;
        props.cz_item_plu = itemPLU;
        this.sendEvent("cz_promo_ordered", props);
    }

    async trackPromoCancelled(promoName: string, promoType: string, itemName: string, itemPrice: number, itemPLU: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_name = promoName;
        props.cz_promo_type = promoType;
        props.cz_item_name = itemName;
        props.cz_item_price = itemPrice;
        props.cz_item_plu = itemPLU;
        this.sendEvent("cz_promo_cancelled", props);
    }

    async trackExtraPromoShown(itemName: string, componentName: string, extraName: string, itemPLU: string, componentPLU: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_type = "extra";
        props.cz_item_name = itemName;
        props.cz_component_name = componentName;
        props.cz_extra_name = extraName;
        props.cz_item_plu = itemPLU;
        props.cz_component_plu = componentPLU;
        this.sendEvent("cz_promo_shown", props);
    }

    async trackExtraPromoOrdered(itemName: string, componentName: string, extraName: string, itemPLU: string, componentPLU: string, priceChange: number): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_type = "extra";
        props.cz_item_name = itemName;
        props.cz_component_name = componentName;
        props.cz_extra_name = extraName;
        props.cz_item_plu = itemPLU;
        props.cz_component_plu = componentPLU;
        props.cz_price_change = priceChange;
        this.sendEvent("cz_promo_ordered", props);
    }

    async trackExtraPromoCancelled(itemName: string, componentName: string, extraName: string, itemPLU: string, componentPLU: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_promo_type = "extra";
        props.cz_item_name = itemName;
        props.cz_component_name = componentName;
        props.cz_extra_name = extraName;
        props.cz_item_plu = itemPLU;
        props.cz_component_plu = componentPLU;
        this.sendEvent("cz_promo_cancelled", props);
    }

    async trackNavOption(optionName: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_option_name = optionName;
        this.sendEvent("cz_nav_option", props);
    }

    async trackFeatureButton(optionName: string): Promise<void> {
        await this.checkInitialised();
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        const props: any = this.commonProps(paramConfig);
        props.cz_option_name = optionName;
        this.sendEvent("cz_feature_button", props);
    }

    /**
     * Properties tracked on all events
     */
    private commonProps(paramConfig: ParamConfig): any {
        const props: any = {};
        props.send_to = this.googleKey;
        props.cz_analytics_version = AnalyticsService.ANALYTICS_VERSION;
        props.cz_app_session_id = this._sessionModel.appSessionId;
        props.cz_scan_id = this._sessionModel.scanId;
        props.cz_beacon_d = this._sessionModel.beaconId;
        props.cz_app_version = AppModuleConstants.VERSION;
        props.cz_client_id = this.clientId;
        props.cz_app_name = this.appName;
        props.cz_client_app = this.clientId + "/" + this.appName;
        props.cz_is_native = paramConfig.nativeMode;
        props.cz_payment_provider = this._paymentModel.paymentProvider;
        if (this._orderingContextModel.currentStore) {
            props.cz_store_name = this._orderingContextModel.currentStore.name;
            props.cz_web_terminal_id = this._orderingContextModel.currentStore.webTerminalId;
            props.cz_venue_id = this._orderingContextModel.currentStore.venueId;
        } else {
            props.cz_web_terminal_id = paramConfig.exclusiveStore;
        }
        const weekday = [];
        weekday[0] = "Sun";
        weekday[1] = "Mon";
        weekday[2] = "Tue";
        weekday[3] = "Wed";
        weekday[4] = "Thu";
        weekday[5] = "Fri";
        weekday[6] = "Sat";
        props.cz_day_of_week = weekday[new Date().getDay()];

        const month = [];
        month[0] = "January";
        month[1] = "February";
        month[2] = "March";
        month[3] = "April";
        month[4] = "May";
        month[5] = "June";
        month[6] = "July";
        month[7] = "August";
        month[8] = "September";
        month[9] = "October";
        month[10] = "November";
        month[11] = "December";
        props.cz_month = month[new Date().getMonth()];

        return props;
    }

    private addStoreStatusProp(props: any): any {
        if (!this._orderingContextModel.currentStore) {
            props.cz_store_status = "noStore";
        } else if (this._orderingContextModel.currentStore.closed) {
            props.cz_store_status = "closed";
        } else if (this._orderingContextModel.currentStore.offline) {
            props.cz_store_status = "offline";
        } else {
            props.cz_store_status = "open";
        }
        return props;
    }

    private sendEvent(eventName: string, props: any): void {
        if (this.analyticsEnabled) {
            gtag("event", eventName, props);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/orchestration/analytics.service.ts